import React, { useCallback, useEffect, useState } from 'react';
import { Header } from './components/Header';
import { HeroSection } from './components/HeroSection';
import { EventSection, RegisterFormData } from './components/EventSection';
import { NewsSection } from './components/NewsSection';
import { SafetySection } from './components/SafetySection';
import { CertificateSection } from './components/CertificateSection';
import { TrainingStatsSection } from './components/TrainingStatsSection';
import { PaymentsSection } from './components/PaymentsSection';
import { AuthModal } from './components/AuthModal';
import { UserCenterModal } from './components/UserCenterModal';
import { PaymentModal } from './components/PaymentModal';
import { Footer } from './components/Footer';

import { api, setCsrfToken } from './api';
import { User, SwimEvent, NewsArticle, SafetyGuide, HomeData, PaymentInfo, QuizQuestion } from './types';
import { SAFETY_QUIZ } from './data/quizData';

export default function App() {
  const [activeTab, setActiveTab] = useState('home');
  const [user, setUser] = useState<User | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [userCenterOpen, setUserCenterOpen] = useState(false);
  const [payment, setPayment] = useState<PaymentInfo | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [settings, setSettings] = useState({ announcement: '', notice_bar: '贵州省黔南州长顺县游泳协会官方网站 | 生命高于一切，防溺水重于泰山', member_fee: '150', member_fee_title: '', emergency_phone: '', about_text: '' });
  const [stats, setStats] = useState({ events: 0, members: 0, certificates: 0, trainings: 0 });
  const [events, setEvents] = useState<SwimEvent[]>([]);
  const [newsList, setNewsList] = useState<NewsArticle[]>([]);
  const [guides, setGuides] = useState<SafetyGuide[]>([]);
  const [booting, setBooting] = useState(true);

  // 导航事件（供跨组件跳转）
  useEffect(() => {
    const handler = (e: Event) => {
      const tab = (e as CustomEvent).detail;
      if (typeof tab === 'string') { setActiveTab(tab); window.scrollTo({ top: 0 }); }
    };
    window.addEventListener('csx-nav', handler);
    return () => window.removeEventListener('csx-nav', handler);
  }, []);

  // 初始加载
  const loadHome = useCallback(async () => {
    try {
      const data = await api<HomeData>('get_home_data');
      setSettings((s) => ({ ...s, ...data.settings }));
      setStats(data.stats);
      setEvents(data.events);
      setNewsList(data.news);
      setGuides(data.guides);
    } catch { /* 首页数据加载失败，静默 */ }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const me = await api<User>('me');
      setUser(me);
      if (me.csrf_token) setCsrfToken(me.csrf_token);
      if (me.settings) setSettings((s) => ({ ...s, ...me.settings }));
    } catch {
      setUser(null);
    }
  }, []);

  const refreshEvents = useCallback(async () => {
    try {
      const list = await api<SwimEvent[]>('get_events');
      setEvents(list);
      const data = await api<HomeData>('get_home_data');
      setStats(data.stats);
    } catch { /* ignore */ }
  }, []);

  const refreshNews = useCallback(async () => {
    try {
      const list = await api<NewsArticle[]>('get_news');
      setNewsList(list);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([refreshUser(), loadHome(), refreshNews()]);
      setBooting(false);
    })();
  }, [loadHome, refreshUser, refreshNews]);

  const handleLogin = (u: User) => {
    setUser(u);
    setAuthOpen(false);
    if (u.csrf_token) setCsrfToken(u.csrf_token);
    refreshEvents();
  };

  const handleLogout = async () => {
    try { await api('logout', {}, 'POST'); } catch { /* ignore */ }
    setUser(null);
    setUserCenterOpen(false);
    setRefreshKey((k) => k + 1);
  };

  const handleRequireLogin = () => setAuthOpen(true);

  // 入会
  const handleJoinMembership = async () => {
    if (!user) { setAuthOpen(true); return; }
    try {
      const data = await api<PaymentInfo>('join_membership', {}, 'POST');
      setPayment(data);
    } catch (ex: any) {
      alert(ex.message || '下单失败');
    }
  };

  // 赛事报名 → 返回支付信息
  const handleRegister = async (ev: SwimEvent, form: RegisterFormData): Promise<PaymentInfo> => {
    const data = await api<PaymentInfo>('register_event', { ...form }, 'POST');
    setPayment(data);
    return data;
  };

  // 支付成功
  const handlePaymentSuccess = async () => {
    setPayment(null);
    setRefreshKey((k) => k + 1);
    await refreshUser();
    await refreshEvents();
  };

  if (booting) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-tr from-sky-600 to-blue-600 animate-pulse" />
          <p className="text-sm text-slate-500 font-bold">长顺县游泳协会官网加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        user={user}
        noticeBar={settings.notice_bar}
        onOpenAuth={() => setAuthOpen(true)}
        onOpenUserCenter={() => setUserCenterOpen(true)}
        onLogout={handleLogout}
      />

      <main>
        {activeTab === 'home' && (
          <>
            <HeroSection onNavigate={setActiveTab} announcement={settings.announcement} stats={stats} />
            <EventSection events={events.slice(0, 3)} user={user} onRegister={handleRegister} onRequireLogin={handleRequireLogin} />
            <NewsSection newsList={newsList.slice(0, 3)} user={user} onJoinMembership={handleJoinMembership} />
            <SafetySection guides={guides.slice(0, 2)} quizQuestions={SAFETY_QUIZ} />
          </>
        )}

        {activeTab === 'events' && (
          <EventSection events={events} user={user} onRegister={handleRegister} onRequireLogin={handleRequireLogin} />
        )}

        {activeTab === 'news' && (
          <NewsSection newsList={newsList} user={user} onJoinMembership={handleJoinMembership} />
        )}

        {activeTab === 'safety' && (
          <SafetySection guides={guides} quizQuestions={SAFETY_QUIZ} />
        )}

        {activeTab === 'certificates' && (
          <CertificateSection user={user} onRequireLogin={handleRequireLogin} />
        )}

        {activeTab === 'stats' && (
          <TrainingStatsSection user={user} onRequireLogin={handleRequireLogin} refreshKey={refreshKey} />
        )}

        {activeTab === 'payments' && (
          <PaymentsSection user={user} onJoinMembership={handleJoinMembership} onRequireLogin={handleRequireLogin} refreshKey={refreshKey} />
        )}
      </main>

      {/* 弹窗 */}
      {authOpen && <AuthModal onClose={() => setAuthOpen(false)} onLogin={handleLogin} />}
      {userCenterOpen && user && (
        <UserCenterModal user={user} onClose={() => setUserCenterOpen(false)} onLogout={handleLogout} refreshKey={refreshKey} onUserUpdated={(u) => setUser(u)} />
      )}
      <PaymentModal payment={payment} onClose={() => setPayment(null)} onSuccess={handlePaymentSuccess} />

      <Footer />
    </div>
  );
}
